/**
 * Facet provenance on the client (search-unification M5.2b).
 *
 * The shop-agent's relaxation cascade drops facets to recover a zero-result
 * search — but it must never drop a filter the shopper set. It tells the two
 * apart by provenance: facets the query generator inferred arrive stamped
 * `source: "generated"`; facets the shopper chose must arrive stamped
 * `source: "caller"`. This module is the one place the client stamps, and
 * the one place it copies facets without losing a stamp.
 *
 * Rules of thumb for the call sites:
 *  - The shopper picked or changed it (filter bar, gender toggle, navbar
 *    category, brand page identity): `callerFacet` / `withCallerValues`.
 *  - Derived by the client from a product, not from the shopper (similar
 *    products, MLT plans): `clientGeneratedFacet` — droppable, like the
 *    server's own inferences.
 *  - Passing an existing facet along: `copyFacet`, never `{ name, values }`.
 */

import type { Facet } from "openapi/shop_agent/client/types.gen";

/** Stage stamped on facets the client derives from a product rather than from the shopper. */
export const CLIENT_GENERATED_STAGE = "cosimo_client_v1";

/** A facet the shopper chose. Relaxation never drops it. */
export function callerFacet(name: string, values: string[]): Facet {
  return { name, values: [...values], source: "caller", stage: null };
}

/**
 * The shopper changed the values of an existing facet (added a suggestion,
 * picked colors, brands, categories). It is theirs now, whatever inferred it.
 * A facet that was already the caller's keeps its stage (the image
 * classifier's `image_user_constraint_v1` is read server-side); a generated
 * facet's stage is dropped with its source, since it no longer applies.
 */
export function withCallerValues(facet: Facet, values: string[]): Facet {
  const stage = facet.source === "caller" ? (facet.stage ?? null) : null;
  return { ...facet, values: [...values], source: "caller", stage };
}

/** A facet the client derived from a product, not from the shopper. Droppable. */
export function clientGeneratedFacet(name: string, values: string[]): Facet {
  return { name, values: [...values], source: "generated", stage: CLIENT_GENERATED_STAGE };
}

/** The facets the shopper set themselves (stamped `caller`). */
export function shopperFacets<T extends Facet>(facets: readonly T[] | null | undefined): T[] {
  return (facets ?? []).filter((facet) => facet.source === "caller");
}

/** Everything except the shopper's own filters: what a search keeps when they clear them. */
export function withoutShopperFacets<T extends Facet>(
  facets: readonly T[] | null | undefined
): T[] {
  return (facets ?? []).filter((facet) => facet.source !== "caller");
}

/** A copy that keeps the stamp. Use instead of rebuilding `{ name, values }`. */
export function copyFacet<T extends Facet>(facet: T): T {
  return { ...facet, values: [...facet.values] };
}
